/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { AbstractObject, uuid } from '@src/app/model/abstract-object';

/** Which kind of sample record the comms-template editor's Test/Preview picker should offer. */
export type CommsSampleKind = 'person' | 'request' | 'invoice';

export class CommsTemplateType extends AbstractObject {
    title: string;
    description: string;
    replacements: string[];
    sampleKind?: CommsSampleKind;

    static BALLOT = {
        id: 'ballot', name: 'Ballot',
        description: 'Prefills a new Ballot\'s content. Will appear as a new option on the new ballot form. Ballot will be created with questions that have this ballot set as their parent, so there is nothing to test.',
    } as CommsTemplateType;

    static TEAMVALUES = ['currentDate', 'paymentTerms', 'legalName', 'logo', 'omcName', 'omcEmailAddress', 'agentName', 'agentLegalName', 'billingAddress'];

    static BILLING = {
        id: 'billing', name: 'Billing',
        description: 'Used when sending charges related to a billing. Placeholders from the charge and the contact to whom the charge is being sent are available. ',
        sampleKind: 'invoice',
        replacements: [
            'fullName', 'address', 'transactionDate', 'financialYear', 'unitName', 'refNr', 'debit',
            'outstanding', 'unitBalance',
            ... this.TEAMVALUES
        ]
    } as CommsTemplateType;

    static DOCUMENT = {
        id: 'document', name: 'Document',
        description: 'Provide content for standard documentation pages to be used accross OMCs'
    } as CommsTemplateType;

    static PERSON = { id: 'person', name: 'Person',
        description: 'Used for ad-hoc emails to a contact (e.g. from the Send Email dialog or Mail Merge). Placeholders for data related to the contact are available',
        sampleKind: 'person',
        replacements: [
            'fullName', 'address', 'unitName', 'outstanding',
            ... this.TEAMVALUES
        ]
    } as CommsTemplateType;

    static POST = {
        id: 'post', name: 'Post',
        description: 'Standard post content that can be reused accross OMCs.'
    } as CommsTemplateType;

    static REQUEST = { id: 'request', name: 'Request',
        description: 'Templates for sending mails related to a service request.',
        sampleKind: 'request',
        replacements: [
            'refNr', 'title', 'description', 'priority', 'owner',
            'unitName', 'unitAddress',
            'personFullName', 'personPhone',
            ... this.TEAMVALUES
        ]
    } as CommsTemplateType;

    static REQUEST_CLOSING = {
        id: 'requestClosing', name: 'Request Closing',
        description: 'Standard text to use when closing a service request.',
        replacements: []
    } as CommsTemplateType;

    static QUESTION = {
        id: 'question', name: 'Question',
        description: 'Questions are linked to ballots, when you create a new ballot it will also create all of the questions for the ballot.'
    } as CommsTemplateType;

    static TYPES: CommsTemplateType[] = [
        CommsTemplateType.BALLOT,
        CommsTemplateType.BILLING,
        CommsTemplateType.DOCUMENT,
        CommsTemplateType.PERSON,
        CommsTemplateType.POST,
        CommsTemplateType.REQUEST,
        CommsTemplateType.REQUEST_CLOSING,
        CommsTemplateType.QUESTION
    ];
}
export class CommsTemplate extends AbstractObject {
    title: string;
    content: string;
    parentId: uuid;
    type: uuid;

    constructor(o: Partial<CommsTemplate> = {}) {
        super(o);
    }

}
